import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { LoanPaymentSummary as PaymentSummaryType, PaynowService } from '../../api/paynowService';
import PaymentDialog from './PaymentDialog';
import { getForeclosureBalance } from '../../api/client';

interface LoanPaymentSummaryProps {
  loanId: number;
  loanNumber: string;
  onPaymentInitiated?: () => void;
}

const LoanPaymentSummary: React.FC<LoanPaymentSummaryProps> = ({
  loanId,
  loanNumber,
  onPaymentInitiated
}) => {
  const [summary, setSummary] = useState<PaymentSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  /** Only true when opened via "Pay Off Loan Now" — regular Make Payment stays editable */
  const [settlementMode, setSettlementMode] = useState(false);
  const [foreclosureBalance, setForeclosureBalance] = useState<any>(null);
  const [loadingForeclosure, setLoadingForeclosure] = useState(false);
  const [foreclosureError, setForeclosureError] = useState<string>('');

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await PaynowService.getLoanPaymentSummary(loanId);
      setSummary(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payment summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchForeclosureBalance();
  }, [loanId]);

  const fetchForeclosureBalance = async () => {
    try {
      setLoadingForeclosure(true);
      setForeclosureError('');
      const data = await getForeclosureBalance(loanId);
      setForeclosureBalance(data);
    } catch (err: any) {
      console.error('Failed to fetch settlement balance:', err);
      setForeclosureError(err.response?.data?.message || 'Failed to fetch settlement balance');
      // Don't show error to user if it's just not available
      if (err.response?.status !== 404) {
        setForeclosureError(err.response?.data?.message || 'Failed to fetch settlement balance');
      }
    } finally {
      setLoadingForeclosure(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openPaymentDialog = (asSettlement: boolean) => {
    setSettlementMode(asSettlement);
    setPaymentDialogOpen(true);
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
    setSettlementMode(false);
  };

  const handlePaymentInitiated = () => {
    handlePaymentDialogClose();
    fetchSummary(); // Refresh summary
    if (onPaymentInitiated) {
      onPaymentInitiated();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardContent>
          <Typography>No payment summary available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Payment Summary</Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Outstanding Balance */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'error.light', 
                color: 'error.contrastText',
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="subtitle2">Outstanding Balance</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.outstandingBalance)}
                </Typography>
              </Box>
            </Grid>

            {/* Total Paid Principal */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'success.light', 
                color: 'success.contrastText',
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="subtitle2">Total Paid (Principal)</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.totalPaid)}
                </Typography>
              </Box>
            </Grid>

            {/* Processing Fee Status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {summary.processingFeePaid ? (
                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  ) : (
                    <PendingIcon sx={{ color: 'warning.main', mr: 1 }} />
                  )}
                  <Typography variant="subtitle1">
                    Processing Fee ({formatCurrency(summary.processingFeeAmount)})
                  </Typography>
                </Box>
                <Chip
                  label={summary.processingFeePaid ? 'Paid' : 'Pending'}
                  color={summary.processingFeePaid ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Overdue Payment Details */}
            {summary.arrearsStatus?.isOverdue && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'error.main', 
                  color: 'error.contrastText',
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ⚠️ OVERDUE INSTALLMENT
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(summary.arrearsStatus.overdueAmount || 0)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>OVERDUE SINCE: {formatDate(summary.nextInstallment?.dueDate)}</strong> | 
                    Days Overdue: {summary.arrearsStatus.daysOverdue} | 
                    Amount: {formatCurrency(summary.arrearsStatus.overdueAmount || 0)}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Arrears Status */}
            {summary.arrearsStatus && summary.arrearsStatus.isOverdue && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'error.dark', 
                  color: 'error.contrastText',
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ⚠️ ACCOUNT IN ARREARS
                  </Typography>
                  <Typography variant="body2">
                    {summary.arrearsStatus.daysOverdue} days overdue | 
                    Amount: {formatCurrency(summary.arrearsStatus.overdueAmount || 0)}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Payment Dates */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Last Payment Date
              </Typography>
              <Typography variant="body1">
                {formatDate(summary.lastPaymentDate)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {summary.arrearsStatus?.isOverdue ? 'Overdue Payment' : 'Next Payment Due'}
              </Typography>
              <Typography variant="body1">
                {summary.arrearsStatus?.isOverdue ? (
                  <Box>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      ⚠️ OVERDUE
                      <br />
                      {formatDate(summary.nextInstallment?.dueDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Days Overdue: {summary.arrearsStatus.daysOverdue}</strong>
                      <br />
                      Amount: {formatCurrency(summary.arrearsStatus.overdueAmount || 0)}
                    </Typography>
                  </Box>
                ) : summary.nextInstallment ? (
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(summary.nextInstallment.dueDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Amount: {formatCurrency(summary.nextInstallment.totalDueForPeriod)}
                    </Typography>
                  </Box>
                ) : (
                  formatDate(summary.nextPaymentDue)
                )}
              </Typography>
            </Grid>

            {/* Settlement Balance (Pay Off Loan) */}
            {foreclosureBalance && foreclosureBalance.totalAmount > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2.5, 
                    bgcolor: 'primary.light', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <AccountBalanceIcon sx={{ mr: 1, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Pay Off Loan (Settlement Amount)
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1.5 }}>
                      {formatCurrency(foreclosureBalance.totalAmount)}
                    </Typography>
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption">Principal</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(foreclosureBalance.principalPortion || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">Interest</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(foreclosureBalance.interestPortion || 0)}
                          </Typography>
                        </Grid>
                        {foreclosureBalance.feeChargesPortion > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="caption">Fees</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(foreclosureBalance.feeChargesPortion || 0)}
                            </Typography>
                          </Grid>
                        )}
                        {foreclosureBalance.penaltyChargesPortion > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="caption">Penalties</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(foreclosureBalance.penaltyChargesPortion || 0)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AccountBalanceIcon />}
                        onClick={() => openPaymentDialog(true)}
                        size="large"
                        sx={{ 
                          minWidth: 220,
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          py: 1.5
                        }}
                      >
                        Pay Off Loan Now
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </>
            )}

            {/* Payment Action */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={() => openPaymentDialog(false)}
                  size="large"
                  sx={{ minWidth: 200 }}
                  disabled={(summary.outstandingBalance || 0) <= 0}
                >
                  Make Payment
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <PaymentDialog
        open={paymentDialogOpen}
        onClose={handlePaymentDialogClose}
        loanId={loanId}
        loanNumber={loanNumber}
        outstandingBalance={summary.outstandingBalance}
        settlementAmount={settlementMode ? foreclosureBalance?.totalAmount : undefined}
        loanCurrency={summary.currency || 'USD'}
        onPaymentInitiated={handlePaymentInitiated}
      />
    </>
  );
};

export default LoanPaymentSummary;
